import { canAccessClinicDemo } from "@/features/clinic-operations/demoAccess"
import { cn } from "@/lib/utils"
import {
  LayoutDashboard,
  Users,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  CheckSquare,
  BarChart3,
  Activity,
  Receipt,
  Settings,
  HelpCircle,
  ChevronDown,
  LogOut,
  Building2,
  ChevronsUpDown,
  PanelLeftClose,
  PanelLeft,
  Eye,
  HeartPulse,
} from "lucide-react"
import React from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuthStore } from "@/stores/authStore"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import {
  useCurrentOrgRole,
  canAccessApcmOps,
  canAccessDemoCalls,
} from "@/lib/useCurrentOrgRole"
import { signOut } from "aws-amplify/auth"
import { useTcmSummary } from "@/features/tcm"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"

interface NavItem {
  href: string
  label: string
  icon: React.ElementType
  badge?: number
}

const mainNavItems: NavItem[] = [
  { href: "/clinic-operations/demo", label: "Clinic Operations · Demo", icon: Building2 },
  { href: "/admin/clinic-operations", label: "Clinic Operations", icon: Building2 },
  { href: "/action-items", label: "Action Items", icon: CheckSquare },
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/patients", label: "Patients", icon: Users },
  { href: "/campaigns", label: "Campaigns", icon: Phone },
  { href: "/calls", label: "Calls", icon: PhoneCall },
  { href: "/analytics", label: "Analytics", icon: BarChart3 },
  { href: "/apcm-ops", label: "APCM Ops", icon: Activity },
  { href: "/tcm", label: "TCM", icon: HeartPulse },
  { href: "/billing-codes", label: "Billing Codes", icon: Receipt },
  { href: "/demo-calls", label: "Demo Calls", icon: PhoneOutgoing },
]

const bottomNavItems: NavItem[] = [
  { href: "/settings", label: "Settings", icon: Settings },
]

/**
 * Where the sidebar content is being rendered.
 *
 * - `desktop`: the persistent `<aside>` (collapsible, tooltips when collapsed)
 * - `drawer`: inside the mobile Sheet (always expanded, no collapse toggle)
 */
export type SidebarVariant = "desktop" | "drawer"

export interface SidebarContentProps {
  variant?: SidebarVariant
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  onOrgClick?: () => void
  onImpersonateClick?: () => void
  /** Called after any nav link is activated (the drawer uses this to close). */
  onNavigate?: () => void
}

/**
 * The sidebar's inner content: logo, nav, bottom section, org switcher and
 * user menu. Rendered by both the desktop `<aside>` and the mobile drawer so
 * the nav definitions live in exactly one place.
 */
export function SidebarContent({
  variant = "desktop",
  collapsed: collapsedProp = false,
  onCollapsedChange,
  onOrgClick,
  onImpersonateClick,
  onNavigate,
}: SidebarContentProps) {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const { organizations, selectedOrganizationId } = useOrganizationsStore()
  const currentOrgRole = useCurrentOrgRole()

  const isDrawer = variant === "drawer"
  // The drawer is always expanded; collapsing only makes sense for the
  // persistent desktop rail.
  const collapsed = isDrawer ? false : collapsedProp

  const selectedOrg = organizations.find((org) => org.id === selectedOrganizationId)
  const showApcmOps = canAccessApcmOps(currentOrgRole, !!user?.is_superuser)
  const showDemoCalls = canAccessDemoCalls(currentOrgRole, !!user?.is_superuser)
  const gatedNavItems: Record<string, boolean> = {
    "/clinic-operations/demo": canAccessClinicDemo(user),
    "/admin/clinic-operations": !!user?.is_superuser,
    "/apcm-ops": showApcmOps,
    "/tcm": showApcmOps,
    "/demo-calls": showDemoCalls,
  }
  const { data: tcmSummary } = useTcmSummary(showApcmOps)
  const tcmClinic = tcmSummary?.clinics.find((c) => c.organization_id === selectedOrganizationId)
  // Pear Admin (a superuser outside every clinic) sees the cross-clinic TCM view, so the badge counts all clinics.
  const tcmOverdue = tcmClinic
    ? tcmClinic.overdue
    : user?.is_superuser
      ? tcmSummary?.clinics.reduce((n, c) => n + c.overdue, 0)
      : undefined
  const visibleNavItems = mainNavItems
    .filter((item) => gatedNavItems[item.href] ?? true)
    .map((item) => (item.href === "/tcm" ? { ...item, badge: tcmOverdue } : item))

  const isActive = (href: string) => {
    if (href === "/action-items") {
      return location.pathname === "/" || location.pathname.startsWith("/action-items")
    }
    const [path] = href.split("?")
    return location.pathname.startsWith(path)
  }

  const NavLink = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href)
    const Icon = item.icon

    const linkContent = (
      <Link
        to={item.href}
        onClick={onNavigate}
        className={cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
          "text-white/70 hover:text-white hover:bg-white/[0.06]",
          // Slightly taller tap targets in the drawer.
          isDrawer && "py-2.5 text-sm",
          active && "bg-white/10 text-white",
          active &&
            "before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-accent",
          collapsed && "justify-center px-2"
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
        {!collapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="rounded-full bg-sidebar-accent/25 px-2 py-0.5 text-[11px] font-semibold text-white tabular-nums">
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    )

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2 border-white/10 bg-pear-emerald-dark text-white">
            {item.label}
            {item.badge !== undefined && item.badge > 0 && (
              <span className="rounded-full bg-white/30 px-2 py-0.5 text-xs font-medium text-white">
                {item.badge}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      )
    }

    return linkContent
  }

  return (
    <div className="flex h-full min-h-0 flex-col text-white">
      {/* Header with Logo */}
      <div
        className={cn("flex h-16 shrink-0 items-center px-5", collapsed && "justify-center px-2")}
      >
        <Link to="/" onClick={onNavigate} className="flex items-center rounded-md" aria-label="Pear home">
          <img
            src={require("@/assets/pear_logo_white.svg").default}
            alt="Pear"
            className={cn("h-7", collapsed && "h-6")}
          />
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 pb-3 pt-2">
        {visibleNavItems.map((item) => (
          <NavLink key={item.href} item={item} />
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="shrink-0 space-y-0.5 border-t border-white/10 p-3">
        {/* Bottom Nav Items */}
        {bottomNavItems.map((item) => (
          <NavLink key={item.href} item={item} />
        ))}

        {/* Collapse Toggle (desktop rail only) */}
        {!isDrawer && (
          <button
            className={cn(
              "flex w-full items-center rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
              "text-white/60 hover:bg-white/[0.06] hover:text-white",
              collapsed && "justify-center px-2"
            )}
            onClick={() => onCollapsedChange?.(!collapsed)}
          >
            {collapsed ? (
              <PanelLeft className="h-[18px] w-[18px]" strokeWidth={1.75} />
            ) : (
              <>
                <PanelLeftClose className="mr-3 h-[18px] w-[18px]" strokeWidth={1.75} />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}

        {/* Organization Switcher */}
        {organizations.length > 0 && (
          <button
            className={cn(
              "mt-2 flex w-full items-center rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[13.5px] transition-colors",
              "text-white hover:bg-white/[0.08]",
              collapsed && "justify-center border-0 bg-transparent px-2"
            )}
            onClick={onOrgClick}
          >
            <Building2 className="h-[18px] w-[18px] shrink-0 text-white/70" strokeWidth={1.75} />
            {!collapsed && (
              <>
                <span className="ml-3 min-w-0 flex-1 truncate text-left font-medium">
                  {selectedOrg?.name || "Select Organization"}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </>
            )}
          </button>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex w-full items-center rounded-lg px-3 py-2 text-[13.5px] transition-colors",
                "text-white/80 hover:bg-white/[0.06] hover:text-white",
                collapsed && "justify-center px-2"
              )}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sidebar-accent/30 text-xs font-semibold text-white">
                {user?.email?.[0]?.toUpperCase() || "U"}
              </div>
              {!collapsed && (
                <>
                  <span className="ml-3 min-w-0 flex-1 truncate text-left">
                    {user?.email}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={collapsed ? "center" : "start"}
            side="top"
            className="w-56"
          >
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium">{user?.email}</p>
              {selectedOrg && (
                <p className="truncate text-xs text-muted-foreground">{selectedOrg.name}</p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" onClick={onNavigate} className="flex items-center">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <HelpCircle className="mr-2 h-4 w-4" />
              Help & Support
            </DropdownMenuItem>
            {/* Gated on the EFFECTIVE user, so it correctly disappears while
                impersonating — the banner's Exit is the way back. */}
            {user?.is_superuser && onImpersonateClick && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onImpersonateClick}>
                  <Eye className="mr-2 h-4 w-4" />
                  View as user…
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => signOut()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

interface AppSidebarProps {
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  onOrgClick?: () => void
  onImpersonateClick?: () => void
}

/**
 * Persistent desktop sidebar. Hidden below `md`; the mobile shell renders the
 * same {@link SidebarContent} inside a Sheet drawer instead.
 */
export function AppSidebar({ collapsed = false, onCollapsedChange, onOrgClick, onImpersonateClick }: AppSidebarProps) {
  return (
    <TooltipProvider>
      <aside
        className={cn(
          "hidden h-full shrink-0 flex-col bg-pear-gradient text-white transition-[width] duration-300 md:flex",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <SidebarContent
          variant="desktop"
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          onOrgClick={onOrgClick}
          onImpersonateClick={onImpersonateClick}
        />
      </aside>
    </TooltipProvider>
  )
}
